import Config from "../api/Config"
import { DataSchemaType, InputOption } from "./Interfaces";

/**
 * Identifies the intended recipient groups for governance & policies.
 * 
 * - 'enterprise': Internal stakeholders and employees.
 * - 'partners'  : External entities, vendors, and contractors.
 * - 'global'    : Universal application across all identities.
*/
export const TARGETED_AUDIENCE = [
    {
        key: "global",
        label: "Global (All Users)",
    },
    {
        key: "enterprise",
        label: "Enterprise Users",
    },
    {
        key: "partners",
        label: "Partners",
    },
]

/**
 * Global Registry of Primitive Data Schema Types.
 * 
 * This serves as the single source of truth for the system's 
 * data architecture. It facilitates the dynamic generation of UI components, 
 * hardware-specific interactions, and backend validation strategies.
 * 
 * ## Core Architectural Roles:
 * - Schema Governance: Enforces a standardized set of data primitives 
 *   (Text, Numeric, Reference, etc.) across all organizational modules.
 * - Contextual Validation: Maps specific, type-safe validation templates 
 *   to their respective data primitives to ensure high-fidelity data integrity.
 * - Interface Orchestration: Provides the nomenclature (labels), 
 *   visual anchoring (icons), and guidance (descriptions) required for 
 *   consistent administrative experiences.
 */
export const DataSchemaTypes: DataSchemaType[] = [
    {
        key: 'text',
        label: "Text Input",
        icon: "fa-input-text",
        desc: "Capture names, emails, and alphanumeric details.",
        validations: [
            { label: 'Standard Text', key: 'alphanumeric' },
            { label: 'Email Address', key: 'email' },
            { label: 'Identity Names', key: 'first_last_names' },
            { label: 'Long Description', key: 'comments_descriptions' },
            { label: 'URL Links', key: 'url_links' }
        ]
    },
    {
        key: 'numeric',
        label: "Numeric Value",
        icon: "fa-input-numeric",
        desc: "Record currency, measurements, and numerical data.",
        validations: [
            { label: 'General Number', key: 'number_formats' },
            { label: 'Currency / Amount', key: 'amounts' },
        ]
    },
    {
        key: 'telephone',
        label: "Phone Number",
        icon: "fa-phone",
        desc: "Capture phone numbers in international format",
        // validations: [
        //     { label: 'International (E.164)', key: 'tel_international' },
        //     { label: 'Local Format', key: 'tel_local' }
        // ]
    },
    {
        key: 'predefined',
        label: "Predefined List",
        icon: "fa-list-ul",
        desc: "Standardized options via dropdowns or radio buttons."
    },
    {
        key: 'reference',
        label: "Reference Input",
        icon: "fa-user-gear",
        desc: "Link to system entities like Managers or Owners."
    },
    {
        key: 'date',
        label: "Date & Time",
        icon: "fa-calendar-day",
        desc: "Log milestones, anniversaries, and timestamps.",
        // validations: [
        //     { label: 'Any Date', key: 'date_any' },
        //     { label: 'Past Dates Only', key: 'date_past' },
        //     { label: 'Future Dates Only', key: 'date_future' },
        //     { label: 'Birth Date (18+)', key: 'date_age_limit' }
        // ]
    },
    {
        key: 'file',
        label: "File Upload",
        icon: "fa-cloud-arrow-up",
        desc: "Store documents, certifications, and media files.",
        validations: [
            { label: 'Images: png/jpeg/jpg', key: 'images' },
            { label: 'Excel Workbooks', key: 'excel_workbooks' },
        ]
    }
];

/**
 * Defines the visual casing rules for text-based input fields.
 * Used by the form engine to apply CSS transformations or 
 * data formatting before persistence.
 */
export const TEXT_TRANSFORMATION: InputOption[] = [
    { label: 'Mixed Case', key: 'mixed' },
    { label: 'Uppercase', key: 'uppercase' },
    { label: 'Lowercase', key: 'lowercase' }
];

/**
 * UI Presentation Strategies for 'predefined' data types.
 * Maps keys to specific high-level components (Headless UI Listbox vs standard inputs).
 */
export const PRESENTATION_MODES: InputOption[] = [
    { label: 'Listbox (Dropdown)',  key: 'listbox' },
    { label: 'Radio Group',         key: 'radio' },
    { label: 'Checkbox Group',      key: 'checkbox' }
]













export const dataTypeMappings: Record<string, any> = {
    text: {
        label: "Text Input",
        value: "text",
        icon: "fa-input-text",
        desc: "Capture names, emails, and alphanumeric details."
    },
    numeric: {
        label: "Numeric / Amounts",
        value: "numeric",
        icon: "fa-input-numeric",
        desc: "Record currency, measurements, and numerical data."
    },
    predefined: {
        label: "Predefined List",
        value: "predefined",
        icon: "fa-list-ul",
        desc: "Standardized options via dropdowns or radio buttons."
    },
    reference: {
        label: "Reference Input",
        value: "reference",
        icon: "fa-user-gear",
        desc: "Link to system entities like Managers or Owners."
    },
    date: {
        label: "Date & Time",
        value: "date",
        icon: "fa-calendar-day",
        desc: "Log milestones, anniversaries, and timestamps."
    },
    file: {
        label: "File Upload",
        value: "file",
        icon: "fa-cloud-arrow-up",
        desc: "Store documents, certifications, and media files."
    },







    freetext: {
        label: "Free Text",
        value: "free-text",
        icon: "fa-input-text",
        desc: "Letters, numbers, and symbols. Ideal for names or titles."
    },
};






/*
* Application Constants
* */
export const APPLICATION = {
    THEME: 'orange',
    GRADIENT: '#A7E0CE',
    ERR_MSG: 'There was an error processing your request.'
}

export const STYLE = {
    W850: { maxWidth: '850px' },
    MAX_WIDTH: { maxWidth: '960px' },
    MARGIN_TOP: { marginTop: '64px' }
}

export const STORAGE_KEYS: any = {
    ENCRYPTION_KEY: '',
}

export const COOKIE_KEYS: any = {
    TOKEN: '__sT7LmNp4',
    SANCTUM: '__akvCbR9',
    STATUS: '__aT4RmL7p',
    THEME: '__uxT4QmY7',
    OPTIONS: {
        path: '/',
        sameSite: Config.COOKIE.SAME_SITE,
        secure: Config.COOKIE.SECURE === "false" ? false : true,
    },

    // New Cookie Keys
    PAGE_COUNT: '__mTz7PgCt'
}

export const dataFormatsArray = [
    {
        key: "freetext",
        label: "Free Text",
        value: "free-text",
        icon: "fa-input-text",
        desc: "Combination of letters, numbers, and symbols. Ideal for names or titles.",
    },
    {
        key: "numeric",
        label: "Numeric",
        value: "numeric",
        icon: "fa-input-numeric",
        desc: "Whole numbers or decimals. Use for amounts or counts.",
    },
    {
        key: "predefined",
        label: "Predefined Options",
        value: "array",
        icon: "fa-list-tree",
        desc: "Create a list and have users select from it.",
    },
    {
        key: "date",
        label: "Date Input",
        value: "date",
        icon: "fa-calendar",
        desc: "Calendar date input, example Starting Date.",
    }, {
        key: "phone",
        label: "Phone Number",
        value: "phone",
        icon: "fa-phone",
        desc: "Supports country codes and standard formats."
    },
];

export const displayStylesArray = [
    {
        key: "radio",
        label: "Radio Buttons",
    },
    {
        key: "checkbox",
        label: "Checkboxes",
    },
    {
        key: "dropdown",
        label: "Dropdown Button",
    },
]

export const textCaseArray = [
    {
        key: "upper",
        label: "Upper Case Only",
    },
    {
        key: "lower",
        label: "Lower Case Only",
    },
    {
        key: "mixed",
        label: "Mixed Case",
    },
]